/**
 * imageUtils.js
 * Utility to normalize Cloudinary image URLs so HEIC files are
 * auto-converted to browser-compatible formats (JPEG / WebP).
 *
 * Cloudinary supports on-the-fly format conversion via URL transformations.
 * Inserting  /f_auto,q_auto/  into the upload URL tells Cloudinary to:
 *   f_auto  → serve WebP to browsers that support it, JPEG otherwise
 *   q_auto  → automatically pick the best quality/size trade-off
 *
 * This works for ALL formats including HEIC, so you never need to change
 * the upload flow — just pipe every image src through normalizeImageUrl().
 */

/**
 * Converts a Cloudinary URL to one that serves a browser-compatible format.
 * Non-Cloudinary URLs are returned unchanged.
 *
 * Example:
 *   IN:  https://res.cloudinary.com/dqsi3ksl5/image/upload/v1780395430/IMG_9075_lhes89.heic
 *   OUT: https://res.cloudinary.com/dqsi3ksl5/image/upload/f_auto,q_auto/v1780395430/IMG_9075_lhes89.heic
 *
 * @param {string} url - Original image URL
 * @returns {string} - Transformed URL safe for all browsers
 */
export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return url;

  // Only process Cloudinary URLs
  if (!url.includes('res.cloudinary.com')) return url;

  // Already has a transformation block — don't double-insert
  if (url.includes('/f_auto') || url.includes('/q_auto')) return url;

  // Insert transformation after "/image/upload/"
  return url.replace(
    '/image/upload/',
    '/image/upload/f_auto,q_auto/'
  );
}

/**
 * Normalises an array of image URLs, filtering out empty/null values.
 *
 * @param {string[]} images
 * @param {string} fallback - URL to use when the array is empty
 * @returns {string[]}
 */
export function normalizeImages(images, fallback = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg') {
  if (!images || images.length === 0) return [fallback];
  const normalized = images
    .filter(Boolean)
    .map(normalizeImageUrl);
  return normalized.length > 0 ? normalized : [fallback];
}